/**
 * Motor de cohortes — agrupa y compara la flota por atributos de riesgo.
 *
 * Implementa el ADR-004. Es una función pura: mismos análisis y mismos
 * atributos, mismas cohortes, en cualquier orden en que lleguen.
 *
 * Dos decisiones que cambian lo que significan los números y por eso están
 * escritas aquí y no escondidas:
 *
 *  1. **Tasa agregada, no promedio de promedios.** La tasa de una cohorte es
 *     `Σ episodios / Σ kilómetros × 100`. Promediar las tasas individuales le
 *     daría el mismo peso a un vehículo de 20 km que a uno de 2.000, y el de
 *     20 km es justo el que tiene la tasa más ruidosa.
 *  2. **Supresión por tamaño mínimo.** Una cohorte con pocos vehículos o pocos
 *     kilómetros no publica cifras. Protege la privacidad y, sobre todo,
 *     impide leer ruido como si fuera señal.
 *
 *  3. **Un vehículo que no pudo medir no entra en la tasa.** Sus ceros no son
 *     conducta. Ver abajo.
 *
 * ## La tercera, que llegó tarde: el cero mudo dentro de una tasa de cohorte
 *
 * Este motor contaba y avisaba con `detectorsBlindedBySampling`, que solo habla
 * de la **cadencia**, y con eso se le escapaba el caso más común de producción:
 * un equipo que reporta cada 10 s —cadencia de sobra— cuyo umbral queda por
 * encima de lo más alto que ese vehículo hizo en todo el período. Ese vehículo
 * no se contaba en ninguna parte, y sus ceros entraban enteros en el numerador
 * de la cohorte mientras sus kilómetros entraban enteros en el denominador: la
 * tasa bajaba, y lo hacía por no haber podido medir.
 *
 * Para una cifra que va a una aseguradora eso no es un redondeo: es una cohorte
 * que parece manejar mejor de lo que se sabe. Y el arreglo es el que la flota ya
 * había tomado (`fleet/fleet-comparison.ts`, motivo `UNMEASURED_FAMILIES`): el
 * vehículo mudo **no compite**, se cuenta aparte y se explica. Aquí «no compite»
 * quiere decir que no entra en las métricas publicadas de su cohorte.
 *
 * Tres consecuencias que se asumen a propósito:
 *
 *  - las métricas de la celda describen a los vehículos que **sí** pudieron
 *    medir, incluidas las de exposición y calidad. Un numerador y un denominador
 *    que no vienen del mismo conjunto de vehículos no son una tasa de nada;
 *  - los mínimos de vehículos y de kilómetros se comprueban sobre ese mismo
 *    conjunto. Una cohorte de cinco vehículos con cuatro mudos publicaría, con la
 *    comprobación vieja, la tasa de **un** vehículo bajo la etiqueta del grupo;
 *  - el vehículo mudo sigue contado en `vehicles`. Excluirlo de la tasa y
 *    además borrarlo del conteo sería esconder el problema en vez de declararlo.
 *
 * La causa se distingue —cadencia o umbral— por la misma razón que en la flota:
 * la primera se corrige configurando el equipo y la segunda calibrando el
 * vehículo contra su propia distribución. Un aviso que las confunda manda a
 * cambiar el equipo equivocado.
 */

import type { DrivingAnalysisV5 } from "../pipeline/driving-analysis-v5.ts";
import type { FamilyMeasurability } from "../report/component-measurability.ts";
import {
  COHORT_DIMENSIONS,
  DIMENSION_LABELS,
  NO_DATA_KEY,
  dimensionValue,
  type CohortDimension,
  type VehicleAttributes,
} from "./attributes.ts";

export const COHORT_ENGINE_VERSION = "cohorts-v1" as const;

/** Un vehículo analizado, listo para entrar en una cohorte. */
export type CohortSubject = {
  deviceId: string;
  plate: string | null;
  attributes: VehicleAttributes;
  analysis: Pick<DrivingAnalysisV5, "status" | "exposure" | "dataQuality" | "technicalDriving">;
  /** Firma del análisis: hace auditable la cohorte hasta el vehículo. */
  analysisSignature: string;
  /** Verdadero si la cadencia dejó ciegos a los detectores (ADR-007). */
  detectorsBlindedBySampling: boolean;
  /**
   * Medibilidad por familia, decidida en el servidor con el perfil aplicado y
   * las mediciones del período delante. Este motor no la vuelve a decidir.
   *
   * Opcional por la misma razón que en `FleetVehicleRow`: una pestaña abierta
   * desde antes del despliegue recibe filas sin el campo, y un motor que las
   * rechazara convertiría una mejora de la tasa en una pantalla en blanco. La
   * ausencia se trata explícitamente en {@link unmeasuredCause} —se cae al
   * criterio anterior, solo cadencia—, no con un `?.` escondido.
   */
  measurability?: FamilyMeasurability[];
};

/**
 * Por qué un vehículo no puede entrar en la tasa de su cohorte, o `null` si
 * puede.
 *
 * La cadencia va primero, con el mismo criterio y el mismo orden que
 * `excludeReason` en la flota: es la causa que NO depende del perfil, se corrige
 * configurando el equipo, y un equipo cegado por cadencia también tiene familias
 * mudas — si mandara el umbral, el aviso pediría recalibrar un umbral que a esa
 * cadencia no va a servir de nada.
 */
export type UnmeasuredCause = "SAMPLING" | "UNMEASURED_FAMILIES";

export function unmeasuredCause(subject: CohortSubject): UnmeasuredCause | null {
  if (subject.detectorsBlindedBySampling) return "SAMPLING";
  // Sin el campo se juzga con el criterio anterior —solo cadencia—, que es el
  // que había: no se inventa una exclusión sobre un dato que no llegó.
  const unmeasured = subject.measurability?.filter((family) => !family.measured) ?? [];
  return unmeasured.length ? "UNMEASURED_FAMILIES" : null;
}

export type CohortThresholds = {
  minimumVehicles: number;
  minimumKilometres: number;
};

export const DEFAULT_THRESHOLDS: CohortThresholds = {
  minimumVehicles: 3,
  minimumKilometres: 100,
};

export type SuppressionReason = "TOO_FEW_VEHICLES" | "TOO_FEW_KILOMETRES";

export type CohortCell = {
  /** Valor por dimensión que define esta cohorte. */
  key: Record<string, string>;
  /** Etiqueta legible, ya ordenada por dimensión. */
  label: string;
  /** Todos los vehículos de la cohorte, midieran o no. */
  vehicles: number;
  /** Vehículos cuyos detectores estaban ciegos por cadencia de muestreo. */
  vehiclesWithBlindedDetectors: number;
  /**
   * Vehículos con alguna familia muda que la cadencia NO explica: el umbral del
   * perfil quedó por encima de todo lo que ese vehículo hizo en el período.
   * Se cuenta aparte de la cadencia porque el arreglo es otro.
   */
  vehiclesWithUnmeasuredFamilies: number;
  /**
   * Los que de verdad sostienen `metrics`. Cuando es menor que `vehicles`, la
   * cifra publicada describe a menos vehículos de los que la etiqueta nombra, y
   * quien la lea tiene derecho a saberlo antes de citarla.
   */
  vehiclesMeasured: number;
  suppressed: boolean;
  suppressionReason: SuppressionReason | null;
  /**
   * `null` cuando la cohorte está suprimida: no se publica nada de ella.
   *
   * Cuando no lo está, describe SOLO a `vehiclesMeasured` vehículos — los que
   * pudieron medir. Incluidas las cifras de exposición y calidad: sacar los ceros
   * mudos del numerador y dejar sus kilómetros en el denominador daría una tasa
   * más falsa que la que este cambio vino a arreglar.
   */
  metrics: CohortMetrics | null;
};

export type CohortMetrics = {
  exposureKm: number;
  exposureHours: number;
  episodes: number;
  moderateOrHigher: number;
  highOrCritical: number;
  critical: number;
  /** Σ episodios / Σ km × 100. Agregada, no promedio de tasas. */
  episodesPer100Km: number | null;
  moderateOrHigherPer100Km: number | null;
  highOrCriticalPer100Km: number | null;
  episodesPerHour: number | null;
  /** Media ponderada por registros, no por vehículo. */
  validPercent: number | null;
};

export type CohortReport = {
  engineVersion: typeof COHORT_ENGINE_VERSION;
  dimensions: CohortDimension[];
  thresholds: CohortThresholds;
  periodEndDate: string;
  /** Total de vehículos que entraron, incluidos los de cohortes suprimidas. */
  subjects: number;
  /** Vehículos excluidos por no tener exposición elegible. */
  withoutExposure: number;
  cells: CohortCell[];
  /** Cobertura del atributo: cuántos vehículos lo tienen y cuántos no. */
  coverage: Array<{
    dimension: CohortDimension;
    label: string;
    withValue: number;
    withoutValue: number;
    coveragePercent: number;
    distinctValues: number;
  }>;
  /**
   * Advertencia de primer nivel cuando parte de la flota no pudo medir — por
   * cadencia o por umbral—: sin esto, una tasa calculada sobre menos vehículos de
   * los que dice la etiqueta se lee como la tasa del grupo entero.
   *
   * Se llamaba `samplingWarning` cuando la única causa que el motor conocía era
   * la cadencia. El nombre viejo sobre el texto nuevo habría sido la misma clase
   * de defecto que este trabajo cierra: un campo que dice una cosa y contiene otra.
   */
  unmeasuredWarning: string | null;
  /** Firma de la comparación: hash de las firmas de los análisis que la componen. */
  compositionDigest: string[];
};

/**
 * Cómo se lee una celda: cuántos vehículos sostienen la cifra, cuántos no, y
 * por qué está suprimida cuando lo está.
 *
 * ## Lo que la tabla mostraba
 *
 * El motor ya decidía bien —`vehiclesMeasured` alimenta las métricas y los
 * mínimos—, y el panel no usaba nada de lo decidido:
 *
 *  - la columna «Vehículos» imprimía `vehicles` (todos) al lado de kilómetros,
 *    episodios y tasas que describen solo a `vehiclesMeasured`. Un numerador y un
 *    denominador de un conjunto, junto a un conteo de otro;
 *  - el distintivo de la fila solo miraba `vehiclesWithBlindedDetectors`, así que
 *    una cohorte de 5 vehículos con 3 mudos **por umbral** —la causa común de
 *    producción— salía «5 · 1.200 km · 4,2/100 km» sin ningún aviso;
 *  - `vehiclesWithUnmeasuredFamilies` y `vehiclesMeasured` no se renderizaban en
 *    ningún sitio, mientras el aviso general del motor prometía que «la celda
 *    dice cuántos son». La promesa era falsa;
 *  - y se veía: la supresión se decide con los que midieron, así que la celda
 *    decía «Suprimida: menos de 3 vehículos» junto a un «5» en la columna.
 *
 * La decisión de qué números y qué palabras van en la fila vive aquí y no en el
 * `.tsx` por el mismo motivo que el resto del módulo: escrita en la pantalla es
 * una segunda copia de la regla, y este repositorio ya perdió dos veces esa
 * apuesta. Aquí además se puede probar sin montar un DOM.
 */
export type CellReading = {
  /**
   * Lo que va en la columna «Vehículos». Dice los dos números cuando no
   * coinciden: el que sostiene la cifra y el que la etiqueta nombra.
   */
  vehiclesLabel: string;
  /** Distintivo de la fila con la causa, o `null` cuando todos midieron. */
  unmeasuredBadge: string | null;
  /** Motivo de supresión redactado, o `null` cuando la celda publica cifras. */
  suppressionNote: string | null;
};

export function readCell(cell: CohortCell, thresholds: CohortThresholds): CellReading {
  // Se arma con los dos contadores por causa y no con la resta
  // `vehicles - vehiclesMeasured`: así el texto no puede quedar diciendo un
  // total que no coincide con las causas que lo componen.
  const bySampling = cell.vehiclesWithBlindedDetectors;
  const byThreshold = cell.vehiclesWithUnmeasuredFamilies;

  return {
    vehiclesLabel:
      cell.vehiclesMeasured === cell.vehicles
        ? `${cell.vehicles}`
        : `${cell.vehiclesMeasured} de ${cell.vehicles}`,

    // Compacto porque es un distintivo de fila: qué significa cada causa y qué
    // hay que hacer con ella lo explica el aviso general, una vez, arriba.
    unmeasuredBadge:
      bySampling > 0 && byThreshold > 0
        ? `${bySampling} sin medición por cadencia y ${byThreshold} por umbral`
        : bySampling > 0
          ? `${bySampling} sin medición por cadencia`
          : byThreshold > 0
            ? `${byThreshold} sin medición por umbral`
            : null,

    /*
     * El motivo nombra el conteo con que se decidió, que es el de los que
     * midieron. «Menos de 3 vehículos» junto a un 5 hacía dudar del 5, del 3 y
     * de la supresión a la vez.
     */
    suppressionNote:
      cell.suppressionReason === "TOO_FEW_VEHICLES"
        ? `Suprimida: solo ${cell.vehiclesMeasured} vehículo(s) pudieron medir, menos de los ` +
          `${thresholds.minimumVehicles} mínimos. Con tan pocos, la tasa describe a esos ` +
          `vehículos y no a la cohorte.`
        : cell.suppressionReason === "TOO_FEW_KILOMETRES"
          ? `Suprimida: los vehículos que pudieron medir no suman los ` +
            `${thresholds.minimumKilometres} km mínimos para publicar una tasa.`
          : null,
  };
}

const round = (value: number) => Math.round(value * 100) / 100;

const rate = (count: number, kilometres: number): number | null =>
  kilometres > 0 ? round((count / kilometres) * 100) : null;

/**
 * Clave canónica de una cohorte. Las dimensiones se ordenan siempre igual, así
 * que cambiar el orden de los filtros en la UI no cambia el agrupamiento
 * (criterio de aceptación CAP-03).
 */
function cohortKey(
  subject: CohortSubject,
  dimensions: CohortDimension[],
  periodEndDate: string,
): { key: Record<string, string>; id: string; label: string } {
  const ordered = [...dimensions].sort();
  const key: Record<string, string> = {};
  for (const dimension of ordered) {
    key[dimension] = dimensionValue(subject.attributes, dimension, periodEndDate);
  }
  return {
    key,
    id: ordered.map((dimension) => `${dimension}=${key[dimension]}`).join("|"),
    label: ordered
      .map((dimension) =>
        key[dimension] === NO_DATA_KEY
          ? `${DIMENSION_LABELS[dimension]}: sin dato`
          : `${DIMENSION_LABELS[dimension]}: ${key[dimension]}`,
      )
      .join(" · "),
  };
}

/**
 * Suma los vehículos que se le den. Recibe SOLO los que pudieron medir: el
 * filtro vive en quien la llama, y aquí no se vuelve a decidir nada.
 */
function accumulate(subjects: CohortSubject[]): CohortMetrics {
  let exposureKm = 0;
  let exposureSeconds = 0;
  let episodes = 0;
  let moderateOrHigher = 0;
  let highOrCritical = 0;
  let critical = 0;
  let validRecords = 0;
  let totalRecords = 0;

  for (const subject of subjects) {
    exposureKm += subject.analysis.exposure.eligibleDistanceKm;
    exposureSeconds += subject.analysis.exposure.eligibleDurationSeconds;

    const counts = subject.analysis.technicalDriving.severityCounts;
    episodes += subject.analysis.technicalDriving.consolidatedEpisodes;
    moderateOrHigher += counts.moderate + counts.high + counts.critical;
    highOrCritical += counts.high + counts.critical;
    critical += counts.critical;

    validRecords += subject.analysis.dataQuality.valid;
    totalRecords += subject.analysis.dataQuality.normalized;
  }

  const exposureHours = exposureSeconds / 3600;

  return {
    exposureKm: round(exposureKm),
    exposureHours: round(exposureHours),
    episodes,
    moderateOrHigher,
    highOrCritical,
    critical,
    episodesPer100Km: rate(episodes, exposureKm),
    moderateOrHigherPer100Km: rate(moderateOrHigher, exposureKm),
    highOrCriticalPer100Km: rate(highOrCritical, exposureKm),
    episodesPerHour: exposureHours > 0 ? round(episodes / exposureHours) : null,
    // Ponderada por registros: un vehículo con 2.000 puntos pesa más que uno
    // con 20 al describir la calidad del conjunto.
    validPercent: totalRecords > 0 ? round((validRecords / totalRecords) * 100) : null,
  };
}

export function buildCohortReport(input: {
  subjects: CohortSubject[];
  dimensions: CohortDimension[];
  periodEndDate: string;
  thresholds?: CohortThresholds;
}): CohortReport {
  const thresholds = input.thresholds ?? DEFAULT_THRESHOLDS;
  const dimensions = [...new Set(input.dimensions)].sort();

  // Un vehículo sin exposición elegible no aporta denominador. Se cuenta
  // aparte en vez de meterlo con cero kilómetros y ensuciar las tasas.
  const withExposure = input.subjects.filter(
    (subject) => subject.analysis.exposure.eligibleDistanceKm > 0,
  );
  const withoutExposure = input.subjects.length - withExposure.length;

  const groups = new Map<string, { meta: ReturnType<typeof cohortKey>; members: CohortSubject[] }>();
  for (const subject of withExposure) {
    const meta = cohortKey(subject, dimensions, input.periodEndDate);
    const existing = groups.get(meta.id);
    if (existing) existing.members.push(subject);
    else groups.set(meta.id, { meta, members: [subject] });
  }

  const cells: CohortCell[] = [...groups.values()]
    .map(({ meta, members }) => {
      // Solo los que pudieron medir alimentan la tasa. Los otros se cuentan por
      // causa, unas líneas más abajo: quedan visibles, no borrados.
      const measuring = members.filter((subject) => unmeasuredCause(subject) === null);
      const metrics = accumulate(measuring);
      // Los mínimos se comprueban sobre los que sostienen la cifra, no sobre los
      // que están en la etiqueta. Con la comprobación vieja, una cohorte de cinco
      // vehículos con cuatro mudos publicaba la tasa de uno solo como si fuera la
      // del grupo, y el mínimo de vehículos —que existe justamente para eso— la
      // dejaba pasar.
      const tooFewVehicles = measuring.length < thresholds.minimumVehicles;
      const tooFewKilometres = metrics.exposureKm < thresholds.minimumKilometres;
      const suppressed = tooFewVehicles || tooFewKilometres;

      return {
        key: meta.key,
        label: meta.label,
        vehicles: members.length,
        vehiclesWithBlindedDetectors: members.filter(
          (subject) => unmeasuredCause(subject) === "SAMPLING",
        ).length,
        vehiclesWithUnmeasuredFamilies: members.filter(
          (subject) => unmeasuredCause(subject) === "UNMEASURED_FAMILIES",
        ).length,
        vehiclesMeasured: measuring.length,
        suppressed,
        // Se reporta el motivo más restrictivo primero, para que quien lea
        // sepa qué tendría que cambiar para que la cohorte se publique.
        suppressionReason: tooFewVehicles
          ? ("TOO_FEW_VEHICLES" as const)
          : tooFewKilometres
            ? ("TOO_FEW_KILOMETRES" as const)
            : null,
        metrics: suppressed ? null : metrics,
      };
    })
    // Orden total y declarado: primero las publicables por tasa descendente,
    // luego las suprimidas, y la etiqueta desempata. Sin el desempate por
    // etiqueta, dos cohortes con la misma tasa saldrían en orden arbitrario.
    .sort((a, b) => {
      if (a.suppressed !== b.suppressed) return a.suppressed ? 1 : -1;
      const rateA = a.metrics?.moderateOrHigherPer100Km ?? -1;
      const rateB = b.metrics?.moderateOrHigherPer100Km ?? -1;
      if (rateA !== rateB) return rateB - rateA;
      if (a.vehicles !== b.vehicles) return b.vehicles - a.vehicles;
      return a.label.localeCompare(b.label, "es");
    });

  const coverage = COHORT_DIMENSIONS.map((dimension) => {
    const values = input.subjects.map((subject) =>
      dimensionValue(subject.attributes, dimension, input.periodEndDate),
    );
    const withValue = values.filter((value) => value !== NO_DATA_KEY).length;
    return {
      dimension,
      label: DIMENSION_LABELS[dimension],
      withValue,
      withoutValue: values.length - withValue,
      coveragePercent: values.length ? round((withValue / values.length) * 100) : 0,
      distinctValues: new Set(values.filter((value) => value !== NO_DATA_KEY)).size,
    };
  });

  /*
   * El aviso nombra las DOS causas y con cuántos vehículos cada una. Un solo
   * número no alcanza: a la cadencia se le cambia la configuración del equipo y
   * al umbral se lo calibra contra la distribución del vehículo, así que un aviso
   * que las sume manda a la mitad de la flota al taller equivocado.
   *
   * No se listan las familias de cada vehículo, como sí hace la flota: aquí la
   * unidad publicada es el grupo, y bajar al detalle por vehículo dentro de una
   * vista que existe para agrupar personas iría contra el motivo por el que esta
   * pantalla suprime cohortes chicas. Quien necesite el detalle lo tiene en el
   * informe del vehículo, que es donde el detalle es legítimo.
   */
  const causes = input.subjects.map((subject) => unmeasuredCause(subject));
  const bySampling = causes.filter((cause) => cause === "SAMPLING").length;
  const byThreshold = causes.filter((cause) => cause === "UNMEASURED_FAMILIES").length;
  const unmeasured = bySampling + byThreshold;
  const unmeasuredWarning =
    unmeasured > 0
      ? `${unmeasured} de ${input.subjects.length} vehículos no pudieron medir alguna familia de ` +
        `episodios en este período` +
        (bySampling > 0
          ? `: ${bySampling} porque reportan con una cadencia que deja fuera de alcance a los ` +
            `detectores —eso se corrige configurando el equipo—`
          : ``) +
        (byThreshold > 0
          ? `${bySampling > 0 ? " y" : ":"} ${byThreshold} porque el umbral del perfil quedó por ` +
            `encima de todo lo que ese vehículo hizo, con la cadencia alcanzando de sobra —eso se ` +
            `corrige calibrando ese vehículo contra su propia distribución—`
          : ``) +
        `. En esos vehículos una tasa de cero significa «no se pudo medir», no «no ocurrió», así ` +
        `que quedaron fuera de las cifras publicadas de su cohorte; siguen contados en ella, y la ` +
        `celda dice cuántos son. Las cifras de cada cohorte describen a sus vehículos medidos. ` +
        `Ver ADR-007.`
      : null;

  return {
    engineVersion: COHORT_ENGINE_VERSION,
    dimensions,
    thresholds,
    periodEndDate: input.periodEndDate,
    subjects: input.subjects.length,
    withoutExposure,
    cells,
    coverage,
    unmeasuredWarning,
    // Ordenadas para que la composición sea comparable entre corridas.
    compositionDigest: input.subjects.map((subject) => subject.analysisSignature).sort(),
  };
}
